import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Box,
  Container,
  Grid,
  Typography,
  Pagination,
  Stack,
} from "@mui/material";
import { Search, Plus, Newspaper, Star } from "lucide-react";
import axios from "axios";
import { EditorState, convertToRaw, ContentState } from "draft-js";
import draftToHtml from "draftjs-to-html";
import htmlToDraft from "html-to-draftjs";

import { colors, customShadows } from "../../../theme";
import { Path } from "../../../static/Constants";
import {
  CommonButton,
  CommonDialog,
  CommonInput,
  CommonChip,
  CommonViewToggle,
} from "../../Components/ui";

import {
  AdminLayout,
  NewsCard,
  NewsForm,
  NewsTable,
  NewsStatsCards,
  NewsPreviewModal,
} from "../../Components/dashboard";

import cinstance from "../../Service/randomcinstance";
import web3 from "../../Service/web3";
import Loaders from "../../Components/Loaders";
import SuccessPopup from "../../Components/SuccessPopup";
import FailedPopup from "../../Components/FailedPopup";

function NewsList() {
  // Navigation & Access State
  const [isAdmin, setIsAdmin] = useState(false);
  const [authChecked, setAuthChecked] = useState(false);
  const [adminAccount, setAdminAccount] = useState("");
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  // News Data State
  const [newsList, setNewsList] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [activeTab, setActiveTab] = useState("all"); // 'all' | 'featured' | 'standard'
  const [viewMode, setViewMode] = useState("grid"); // 'grid' | 'table'

  // Pagination State
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const limit = 12;

  // Dialog & Modal States
  const [showAddPopup, setShowAddPopup] = useState(false);
  const [showEditPopup, setShowEditPopup] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [deleteItemId, setDeleteItemId] = useState(null);
  const [previewArticle, setPreviewArticle] = useState(null);

  // Form State
  const [newsTitle, setNewsTitle] = useState("");
  const [editorState, setEditorState] = useState(EditorState.createEmpty());
  const [file, setFile] = useState(null);
  const [existingImageUrl, setExistingImageUrl] = useState("");
  const [updateNewsId, setUpdateNewsId] = useState(null);
  const [formError, setFormError] = useState("");
  const [validated, setValidated] = useState(false);

  // Success / Feedback States
  const [successMessage, setSuccessMessage] = useState("");
  const [showSuccess, setShowSuccess] = useState(false);
  const [showFailed, setShowFailed] = useState(false);

  const apiUrl = process.env.REACT_APP_API_KEY;

  // Helper: Convert HTML string to draftjs EditorState
  const setEditorStateFromDescription = (description) => {
    if (!description) {
      setEditorState(EditorState.createEmpty());
      return;
    }
    try {
      const contentBlock = htmlToDraft(description);
      if (contentBlock && contentBlock.contentBlocks) {
        const contentState = ContentState.createFromBlockArray(
          contentBlock.contentBlocks,
          contentBlock.entityMap,
        );
        setEditorState(EditorState.createWithContent(contentState));
      } else {
        setEditorState(EditorState.createEmpty());
      }
    } catch {
      setEditorState(EditorState.createEmpty());
    }
  };

  // Reset form states
  const handleCloseForm = () => {
    setNewsTitle("");
    setFile(null);
    setExistingImageUrl("");
    setEditorState(EditorState.createEmpty());
    setFormError("");
    setValidated(false);
    setShowAddPopup(false);
    setShowEditPopup(false);
  };

  // Fetch news data
  const handleFetchData = useCallback(async () => {
    if (window.ethereum || web3) {
      window.web3 = web3;
      try {
        setLoading(true);
        let contractAdmin = "";
        try {
          if (cinstance?.methods?.admin) {
            contractAdmin = await cinstance.methods.admin().call();
          }
        } catch (adminErr) {
          console.warn("Could not fetch contract admin:", adminErr);
        }

        let accounts = [];
        if (window.ethereum?.request) {
          accounts = await window.ethereum.request({ method: "eth_accounts" });
        }
        if (!accounts || accounts.length === 0) {
          accounts = await web3.eth.getAccounts();
        }
        const userAccount =
          accounts && accounts.length > 0
            ? accounts[0]
            : window.ethereum?.selectedAddress || "";
        setAdminAccount(userAccount);

        const isUserAdmin = Boolean(
          contractAdmin &&
            userAccount &&
            contractAdmin.toLowerCase() === userAccount.toLowerCase(),
        );
        setIsAdmin(isUserAdmin);
        setAuthChecked(true);

        if (isUserAdmin) {
          const skip = currentPage * limit;
          const getNewsListFromLocalStorage = () => {
            const savedNews = localStorage.getItem("newsAdmin");
            if (savedNews) {
              const parsed = JSON.parse(savedNews);
              setNewsList(parsed);
              setTotalCount(parsed.length);
              setTotalPages(Math.ceil(parsed.length / limit) || 1);
            }
          };

          try {
            const response = await fetch(
              `${apiUrl}/news/getAllNews?limit=${limit}&skip=${skip}`,
              {
                method: "GET",
                headers: {
                  Authorization: userAccount || "",
                },
              },
            );

            if (response.ok) {
              const data = await response.json();
              const result = Array.isArray(data.result) ? data.result : [];
              setNewsList(result);
              const count =
                typeof data.count === "number" ? data.count : result.length;
              setTotalCount(count);
              setTotalPages(Math.ceil(count / limit) || 1);
              localStorage.setItem("newsAdmin", JSON.stringify(result));
            } else {
              getNewsListFromLocalStorage();
            }
          } catch (fetchErr) {
            console.error("API error fetching news:", fetchErr);
            getNewsListFromLocalStorage();
          }
        } else {
          setIsAdmin(false);
        }
      } catch (authErr) {
        console.error("Admin verification error:", authErr);
        setIsAdmin(false);
        setAuthChecked(true);
      } finally {
        setLoading(false);
      }
    }
  }, [apiUrl, currentPage, limit]);

  useEffect(() => {
    handleFetchData();
  }, [handleFetchData]);

  // Create News Article Handler
  const handleCreateNews = async (event) => {
    event.preventDefault();
    setValidated(true);
    setFormError("");

    const hasText =
      editorState &&
      editorState.getCurrentContent &&
      editorState.getCurrentContent().hasText();

    if (!newsTitle || newsTitle.trim() === "" || !hasText) {
      setFormError("Please fill in both the news headline and content.");
      return;
    }

    setActionLoading(true);
    try {
      const contentState = editorState.getCurrentContent();
      const newsDescHTML = draftToHtml(convertToRaw(contentState));

      const formData = new FormData();
      formData.append("title", newsTitle.trim());
      formData.append("description", newsDescHTML);
      if (file) {
        formData.append("file", file);
      }

      const accounts = await web3.eth.getAccounts();
      const response = await axios.post(`${apiUrl}/news/createNews`, formData, {
        headers: {
          Authorization: accounts[0] || "",
        },
      });

      if (response.status === 201 || response.status === 200) {
        handleCloseForm();
        setSuccessMessage("News article published successfully!");
        setShowSuccess(true);
        handleFetchData();
      } else {
        setFormError("Failed to publish news article. Please try again.");
        setShowFailed(true);
      }
    } catch (err) {
      console.error("Error creating news:", err);
      setFormError("Failed to publish news article. Please check your inputs.");
      setShowFailed(true);
    } finally {
      setActionLoading(false);
    }
  };

  // Edit News Article Handler
  const handleUpdateNews = async (event) => {
    event.preventDefault();
    setValidated(true);
    setFormError("");

    const hasText =
      editorState &&
      editorState.getCurrentContent &&
      editorState.getCurrentContent().hasText();

    if (!newsTitle || newsTitle.trim() === "" || !hasText) {
      setFormError("Please fill in both the news headline and content.");
      return;
    }

    setActionLoading(true);
    try {
      const contentState = editorState.getCurrentContent();
      const newsDescHTML = draftToHtml(convertToRaw(contentState));

      const formData = new FormData();
      formData.append("title", newsTitle.trim());
      formData.append("description", newsDescHTML);
      if (file) {
        formData.append("file", file);
      }

      const accounts = await web3.eth.getAccounts();
      const response = await axios.patch(
        `${apiUrl}/news/updateNews/${updateNewsId}`,
        formData,
        {
          headers: {
            Authorization: accounts[0] || "",
          },
        },
      );

      if (response.status === 200) {
        handleCloseForm();
        setSuccessMessage("News article updated successfully!");
        setShowSuccess(true);
        handleFetchData();
      } else {
        setFormError("Failed to update news article. Please try again.");
        setShowFailed(true);
      }
    } catch (err) {
      console.error("Error updating news:", err);
      setFormError("Failed to update news article.");
      setShowFailed(true);
    } finally {
      setActionLoading(false);
    }
  };

  // Delete News Handler
  const handleDeleteNews = async () => {
    if (!deleteItemId) return;
    setActionLoading(true);
    try {
      const accounts = await web3.eth.getAccounts();
      const response = await axios.delete(
        `${apiUrl}/news/delete/${deleteItemId}`,
        {
          headers: {
            Authorization: accounts[0] || "",
          },
        },
      );

      if (response.status === 200) {
        const updated = newsList.filter((item) => item.id !== deleteItemId);
        setNewsList(updated);
        localStorage.setItem("newsAdmin", JSON.stringify(updated));
        setShowDelete(false);
        setDeleteItemId(null);
        setSuccessMessage("News article deleted successfully!");
        setShowSuccess(true);
        handleFetchData();
      } else {
        setShowDelete(false);
        setShowFailed(true);
      }
    } catch (err) {
      console.error("Error deleting news:", err);
      setShowDelete(false);
      setShowFailed(true);
    } finally {
      setActionLoading(false);
    }
  };

  // Toggle Featured Handler
  const handleToggleFeature = async (newsId, currentFeatured) => {
    try {
      const accounts = await web3.eth.getAccounts();
      // Optimistic update
      setNewsList((prev) =>
        prev.map((item) =>
          item.id === newsId ? { ...item, featured: !currentFeatured } : item,
        ),
      );

      const response = await axios.put(
        `${apiUrl}/news/featured/${newsId}`,
        {},
        {
          headers: {
            Authorization: accounts[0] || "",
          },
        },
      );

      if (response.status !== 200) {
        // Revert on failure
        setNewsList((prev) =>
          prev.map((item) =>
            item.id === newsId ? { ...item, featured: currentFeatured } : item,
          ),
        );
        setShowFailed(true);
      }
    } catch (err) {
      console.error("Error toggling feature status:", err);
      // Revert on error
      setNewsList((prev) =>
        prev.map((item) =>
          item.id === newsId ? { ...item, featured: currentFeatured } : item,
        ),
      );
      setShowFailed(true);
    }
  };

  // Open Edit Modal with article preloaded
  const handleOpenEdit = (article) => {
    setNewsTitle(article.title || "");
    setEditorStateFromDescription(article.description || "");
    setExistingImageUrl(article.imageUrl || "");
    setFile(null);
    setUpdateNewsId(article.id);
    setFormError("");
    setValidated(false);
    setShowEditPopup(true);
  };

  // Filtered Articles based on search & active tab
  const filteredNews = useMemo(() => {
    if (!Array.isArray(newsList)) return [];

    return newsList.filter((item) => {
      // Tab filter
      if (activeTab === "featured" && !item.featured) return false;
      if (activeTab === "standard" && item.featured) return false;

      // Search query filter
      if (searchQuery.trim() !== "") {
        const q = searchQuery.toLowerCase();
        const titleMatch = item.title && item.title.toLowerCase().includes(q);
        const descMatch =
          item.description && item.description.toLowerCase().includes(q);
        return titleMatch || descMatch;
      }

      return true;
    });
  }, [newsList, activeTab, searchQuery]);

  // Metric counts
  const totalArticlesCount = newsList.length;
  const featuredArticlesCount = newsList.filter((i) => i.featured).length;
  const standardArticlesCount = totalArticlesCount - featuredArticlesCount;
  const latestDate =
    newsList.length > 0 && (newsList[0].updatedOn || newsList[0].createdOn)
      ? new Date(
          newsList[0].updatedOn || newsList[0].createdOn,
        ).toLocaleDateString()
      : null;

  return (
    <AdminLayout
      title="News & Announcements"
      subtitle="Create, edit, feature, and broadcast updates to the AutoBET ecosystem."
      breadcrumbs={[
        { label: "Dashboard", href: Path.admin },
        { label: "News Management" },
      ]}
      headerActions={
        <CommonButton
          variant="primary"
          startIcon={<Plus size={18} />}
          onClick={() => {
            handleCloseForm();
            setShowAddPopup(true);
          }}
        >
          Add News
        </CommonButton>
      }
      creatorAddress={adminAccount}
      authChecked={authChecked}
      isAuthorized={isAdmin}
      accessDeniedProps={{
        message:
          "You must be an administrator to access the News Management console.",
      }}
    >
      {loading && <Loaders />}

      {/* Metrics Ribbon */}
      <Box>
        <NewsStatsCards
          totalArticles={totalArticlesCount}
          featuredCount={featuredArticlesCount}
          latestPublishedDate={latestDate}
        />
      </Box>

      {/* Filter & Search Bar */}
      <Box
        sx={{
          p: 2,
          mb: 3,
          borderRadius: "16px",
          bgcolor: colors.background.paper,
          border: `1px solid ${colors.dashboard.cardBorder}`,
          boxShadow: customShadows.cardFlat,
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          alignItems: { xs: "stretch", md: "center" },
          justifyContent: "space-between",
          gap: 2,
        }}
      >
        {/* Category Filter Chips */}
        <Stack
          direction="row"
          spacing={1}
          useFlexGap
          sx={{ flexWrap: "wrap", alignItems: "center" }}
        >
          <CommonChip
            variant="filter"
            label={`All News (${totalArticlesCount})`}
            selected={activeTab === "all"}
            onClick={() => setActiveTab("all")}
          />
          <CommonChip
            variant="filter"
            label={`Featured (${featuredArticlesCount})`}
            selected={activeTab === "featured"}
            icon={
              <Star
                size={13}
                fill={activeTab === "featured" ? "#fff" : "none"}
              />
            }
            onClick={() => setActiveTab("featured")}
          />
          <CommonChip
            variant="filter"
            label={`Standard (${standardArticlesCount})`}
            selected={activeTab === "standard"}
            onClick={() => setActiveTab("standard")}
          />
        </Stack>

        {/* Search & View Switcher */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            flexWrap: "wrap",
          }}
        >
          <CommonInput
            placeholder="Search articles by title or keyword..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            width={{ xs: "100%", sm: "280px" }}
            startAdornment={<Search size={18} color={colors.slate[400]} />}
          />

          {/* View Mode Switcher */}
          <CommonViewToggle value={viewMode} onChange={setViewMode} />
        </Box>
      </Box>

      {/* Main Articles View (Grid or Table) */}
      {filteredNews.length === 0 ? (
        <Box
          sx={{
            py: 8,
            px: 3,
            textAlign: "center",
            bgcolor: colors.background.paper,
            borderRadius: "16px",
            border: `1px dashed ${colors.slate[300]}`,
          }}
        >
          <Box
            sx={{
              width: 64,
              height: 64,
              borderRadius: "50%",
              bgcolor: colors.slate[100],
              color: colors.slate[400],
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mx: "auto",
              mb: 2,
            }}
          >
            <Newspaper size={32} />
          </Box>
          <Typography
            variant="h6"
            sx={{ fontWeight: 700, color: colors.slate[800], mb: 0.5 }}
          >
            No news articles found
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: colors.slate[500], maxWidth: 400, mx: "auto", mb: 3 }}
          >
            {searchQuery
              ? `No results match your search "${searchQuery}". Try a different keyword or filter.`
              : "No announcements have been published yet. Click below to add your first article."}
          </Typography>
          {!searchQuery && (
            <CommonButton
              variant="primary"
              startIcon={<Plus size={16} />}
              onClick={() => {
                handleCloseForm();
                setShowAddPopup(true);
              }}
            >
              Create News Article
            </CommonButton>
          )}
        </Box>
      ) : viewMode === "grid" ? (
        <Box>
          <Grid container spacing={3}>
            {filteredNews.map((item) => (
              <Grid
                item
                xs={12}
                sm={6}
                lg={4}
                xl={3}
                key={`news-card-${item.id}`}
              >
                <NewsCard
                  item={item}
                  onEdit={handleOpenEdit}
                  onDelete={(id) => {
                    setDeleteItemId(id);
                    setShowDelete(true);
                  }}
                  onToggleFeature={handleToggleFeature}
                  onPreview={(art) => setPreviewArticle(art)}
                />
              </Grid>
            ))}
          </Grid>
        </Box>
      ) : (
        <NewsTable
          newsList={filteredNews}
          loading={loading}
          page={currentPage}
          limit={limit}
          onEdit={handleOpenEdit}
          onDelete={(id) => {
            setDeleteItemId(id);
            setShowDelete(true);
          }}
          onToggleFeature={handleToggleFeature}
          onPreview={(art) => setPreviewArticle(art)}
        />
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <Box
          sx={{
            mt: 4,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 2,
          }}
        >
          <Typography variant="body2" sx={{ color: colors.slate[500] }}>
            Showing page {currentPage + 1} of {totalPages} ({totalCount} total
            articles)
          </Typography>

          <Pagination
            count={totalPages}
            page={currentPage + 1}
            onChange={(_, p) => setCurrentPage(p - 1)}
            color="primary"
            shape="rounded"
            sx={{
              "& .MuiPaginationItem-root": {
                borderRadius: "8px",
                fontWeight: 600,
              },
            }}
          />
        </Box>
      )}

      {/* Delete Confirmation Modal */}
      <CommonDialog
        open={showDelete}
        onClose={() => setShowDelete(false)}
        title="Delete Article"
        maxWidth="xs"
        fullWidth
        actions={
          <Box
            sx={{
              display: "flex",
              gap: 1.5,
              justifyContent: "flex-end",
              width: "100%",
            }}
          >
            <CommonButton
              variant="cancel"
              onClick={() => setShowDelete(false)}
              disabled={actionLoading}
            >
              Cancel
            </CommonButton>

            <CommonButton
              variant="danger"
              onClick={handleDeleteNews}
              loading={actionLoading}
            >
              Delete
            </CommonButton>
          </Box>
        }
      >
        <Box sx={{ p: 2 }}>
          <Typography
            variant="body1"
            sx={{ color: colors.slate[700], lineHeight: 1.6 }}
          >
            Are you sure you want to permanently delete this news article? This
            action cannot be undone.
          </Typography>
        </Box>
      </CommonDialog>

      {/* Add News Dialog */}
      <NewsForm
        open={showAddPopup}
        onClose={handleCloseForm}
        onSubmit={handleCreateNews}
        isEdit={false}
        newsTitle={newsTitle}
        setNewsTitle={setNewsTitle}
        file={file}
        setFile={setFile}
        existingImageUrl={existingImageUrl}
        setExistingImageUrl={setExistingImageUrl}
        editorState={editorState}
        setEditorState={setEditorState}
        loading={actionLoading}
        error={formError}
        validated={validated}
      />

      {/* Edit News Dialog */}
      <NewsForm
        open={showEditPopup}
        onClose={handleCloseForm}
        onSubmit={handleUpdateNews}
        isEdit={true}
        newsTitle={newsTitle}
        setNewsTitle={setNewsTitle}
        file={file}
        setFile={setFile}
        existingImageUrl={existingImageUrl}
        setExistingImageUrl={setExistingImageUrl}
        editorState={editorState}
        setEditorState={setEditorState}
        loading={actionLoading}
        error={formError}
        validated={validated}
      />

      {/* Article Preview Modal */}
      <NewsPreviewModal
        open={Boolean(previewArticle)}
        onClose={() => setPreviewArticle(null)}
        article={previewArticle}
      />

      {/* Legacy Popups for compatibility */}
      <SuccessPopup
        show={showSuccess}
        setShowModal={(e) => setShowSuccess(e)}
        message={successMessage || "Operation completed successfully!"}
      />
      <FailedPopup show={showFailed} setShowModal={(e) => setShowFailed(e)} />
    </AdminLayout>
  );
}

export default NewsList;
